import api from "./api";

export const UNITS = ["g", "kg", "ml", "l", "no", "unit"];
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];
export const DAY_EN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const DAY_HI = ["रविवार", "सोमवार", "मंगलवार", "बुधवार", "गुरुवार", "शुक्रवार", "शनिवार"];
export const DAY_OR = ["ରବିବାର", "ସୋମବାର", "ମଙ୍ଗଳବାର", "ବୁଧବାର", "ଗୁରୁବାର", "ଶୁକ୍ରବାର", "ଶନିବାର"];
export const dayName = (wd, lang) => (lang === "hi" ? DAY_HI : lang === "or" ? DAY_OR : DAY_EN)[wd];
export const CLASS_COLORS = ["#DB2777", "#2563EB", "#059669", "#EA580C", "#7C3AED", "#0891B2", "#CA8A04", "#475569"];
export const ACTIVE_KEY = "pm_active_calc";
export const DRAFT_KEY = "pm_builder_draft";

export const slug = (s, taken = []) => {
  let base = String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 30) || "x";
  let k = base, n = 2;
  while (taken.includes(k)) { k = `${base}_${n}`; n += 1; }
  return k;
};

export function blankConfig() {
  const today = new Date();
  const fy = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const menu = {};
  for (let wd = 0; wd < 7; wd += 1) menu[String(wd)] = { serving: wd !== 0, items: [], display: "" };
  return {
    name: "", state: "", language: "en",
    classes: [
      { key: "primary", name: "Primary (I–V)", name_local: "", color: CLASS_COLORS[1], emoji: "🎒" },
      { key: "upper_primary", name: "Upper Primary (VI–VIII)", name_local: "", color: CLASS_COLORS[2], emoji: "📘" },
    ],
    items: [], groups: [], menu,
    periods: [{ name: "Current rates", effective_from: `${fy}-04-01`, effective_to: null, auto_balance_item: null, classes: {} }],
  };
}

const num = (v) => v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v)) && Number(v) >= 0;

// Client-side step checks (server re-validates everything on save).
export function stepErrors(cfg, step) {
  const e = [];
  if (step === 1) {
    if (!cfg.name.trim()) e.push("name");
    if (!cfg.state) e.push("state");
  }
  if (step === 2) {
    if (!cfg.classes.length) e.push("classes_min");
    if (cfg.classes.some((c) => !c.name.trim())) e.push("class_name");
  }
  if (step === 3) {
    if (!cfg.items.length) e.push("items_min");
    if (cfg.items.some((i) => !i.name.trim())) e.push("item_name");
    for (const p of cfg.periods) for (const c of cfg.classes) for (const it of Object.values(p.classes?.[c.key]?.items || {})) {
      if (!num(it.qty) || !num(it.rate)) { e.push("rates_num"); return e; }
    }
  }
  if (step === 4) {
    if (!WEEKDAYS.some((wd) => cfg.menu[String(wd)]?.serving)) e.push("menu_serving");
    if (WEEKDAYS.some((wd) => cfg.menu[String(wd)]?.serving && !(cfg.menu[String(wd)].items || []).length)) e.push("menu_items");
  }
  if (step === 5) {
    if (!cfg.periods.length) e.push("periods_min");
    if (cfg.periods.some((p) => !p.effective_from || (p.effective_to && p.effective_to < p.effective_from))) e.push("period_dates");
    const s = [...cfg.periods].sort((a, b) => (a.effective_from > b.effective_from ? 1 : -1));
    for (let i = 1; i < s.length; i += 1) if (!s[i - 1].effective_to || s[i - 1].effective_to >= s[i].effective_from) { e.push("period_overlap"); break; }
  }
  return e;
}

// Numeric coercion before sending to the server (inputs keep raw strings while typing).
export function normalize(cfg) {
  const out = JSON.parse(JSON.stringify(cfg));
  out.periods.forEach((p) => {
    p.effective_to = p.effective_to || null;
    Object.values(p.classes || {}).forEach((cr) => {
      cr.target_total = cr.target_total === "" || cr.target_total == null ? null : Number(cr.target_total);
      Object.values(cr.items || {}).forEach((it) => {
        it.qty = Number(it.qty || 0); it.rate = Number(it.rate || 0);
        if (it.price !== undefined) it.price = Number(it.price || 0);
      });
    });
  });
  out.groups = (out.groups || []).map((g) => ({ ...g, piece_cost: g.piece_cost === "" || g.piece_cost == null ? null : Number(g.piece_cost) }));
  out.items = out.items.map((i) => { const x = { ...i }; if (!x.group) delete x.group; return x; });
  return out;
}

export const getActiveCalc = () => { try { return localStorage.getItem(ACTIVE_KEY) || "odisha"; } catch { return "odisha"; } };
export const setActiveCalc = (id) => { try { localStorage.setItem(ACTIVE_KEY, id || "odisha"); } catch { /* noop */ } };

export const calcApi = {
  list: () => api.get("/calculators").then((r) => r.data.calculators),
  get: (id) => api.get(`/calculators/${id}`).then((r) => r.data.calculator),
  create: (config, publish) => api.post("/calculators", { config, publish }).then((r) => r.data.calculator),
  update: (id, config, publish) => api.put(`/calculators/${id}`, { config, publish }).then((r) => r.data.calculator),
  remove: (id) => api.delete(`/calculators/${id}`),
  odishaTemplate: () => api.get("/calculators/templates/odisha").then((r) => r.data.config),
  preview: (config, date, students) => api.post("/calculators/preview", { config, date, students }).then((r) => r.data.result),
};

export const fmtQty = (q, unit) => {
  const n = Math.round(Number(q) * 1000) / 1000;
  if (unit === "g" && n >= 1000) return `${(n / 1000).toFixed(3)} kg`;
  if (unit === "ml" && n >= 1000) return `${(n / 1000).toFixed(3)} l`;
  return `${n} ${unit}`;
};
